import { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useHandleSubmitWrongPolicyNoCDV } from 'forms/hooks';
import { useDispatch } from 'react-redux';

import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import { resetForm } from '@gef-ui/form-core/stream/transfer/control';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import ComponentLoader from 'components/molecules/ComponentLoader';
import { useIgnoreErrorsWhen } from 'components/molecules/ComponentLoader/hooks';
import { MaxLengths } from 'constants/forms';
import fetch from 'features/common/fetch';
import { closeFeedback, postFeedback, resetTaskFeedback } from 'features/views/taskFeedback/actions';
import { FIELDS, TASK_FEEDBACK_FORM_NAME } from 'features/views/taskFeedback/form';

export const FeedbackConfirm = ({ taskId, taskTypeId, statusId, feedbackData = {} }) => {
	const dispatch = useDispatch();
	const next = useControlNext(TASK_FEEDBACK_FORM_NAME);

	const handleConfirm = useCallback(() => {
		const { finishTypeId } = feedbackData;
		dispatch(postFeedback(taskId, taskTypeId, finishTypeId));
	}, [dispatch, feedbackData, taskId, taskTypeId]);

	const handleSubmitWrongPolicyNoCDV = useHandleSubmitWrongPolicyNoCDV({ continueFn: handleConfirm });

	const handleCancel = useCallback(() => {
		next(resetForm());
		dispatch(closeFeedback(taskTypeId));
	}, [dispatch, next, taskTypeId]);

	useEffect(
		() => () => {
			dispatch(resetTaskFeedback(taskTypeId));
		},
		[dispatch, taskTypeId],
	);

	// feedbackPossible is defined by FINISH_TYPE codelist
	// because feedbackData == finishTypeCodelistItem
	// defines whatever feedback will be with added policy/proposal number
	const { feedbackPossible } = feedbackData;
	const finished = statusId === 3;

	const feedbackHeader = useMemo(() => {
		let header = getLabelMessage('taskFeedback:confirmHeader');

		if (!finished && feedbackPossible) {
			header = getLabelMessage('taskFeedback:confirmHeaderPossibleCP');
		}

		return header;
	}, [finished, feedbackPossible]);

	const ignoreErrorsForTaskFinish = useIgnoreErrorsWhen({
		fetchStorePath: `${fetch.constants.urls.taskFinish}.${taskId}`,
		attributePath: ['metaData', 'parsedContent', 'status'],
		value: 403,
	});

	return (
		<Stack
			spacing={2}
			sx={{
				alignItems: 'center',
			}}
		>
			<Typography variant="h4" color="primary.main">
				{feedbackHeader}
			</Typography>
			<Grid container sx={{ width: '100%' }}>
				<Grid size={12}>
					{!finished && (
						<Grid
							container
							spacing={2}
							sx={{
								justifyContent: 'center',
							}}
						>
							{feedbackPossible && (
								<Tooltip title={getLabelMessage('tooltips:policyNo')}>
									<Grid size={4}>
										<TextField
											size="small"
											fieldName={FIELDS.policyNumber}
											label={getLabelMessage('taskFeedback:policyNo')}
											slotProps={{
												htmlInput: {
													maxLength: MaxLengths.POLICY_NO,
												},
											}}
										/>
									</Grid>
								</Tooltip>
							)}
							<Grid size={12} />
						</Grid>
					)}

					<Grid container>
						<Grid size={12}>
							<Grid
								container
								spacing={3}
								sx={{
									justifyContent: 'center',
								}}
							>
								<Grid size={3}>
									<Button variant="outlined" onClick={handleCancel} fullWidth>
										{getLabelMessage('taskFeedback:cancel')}
									</Button>
								</Grid>
								<Grid size={3}>
									<ComponentLoader
										fetchStorePath={`${fetch.constants.urls.taskFinish}.${taskId}`}
										ignoreErrors={ignoreErrorsForTaskFinish}
									>
										<SubmitButton
											variant="contained"
											onClick={handleConfirm}
											onClickError={handleSubmitWrongPolicyNoCDV}
											fullWidth
											text={getLabelMessage('taskFeedback:save')}
										/>
									</ComponentLoader>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Stack>
	);
};

FeedbackConfirm.propTypes = {
	feedbackData: PropTypes.object,
	parameters: PropTypes.array,
	statusId: PropTypes.number,
	taskId: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
	taskTypeId: PropTypes.number,
};

export default FeedbackConfirm;
